import React, { useRef, Component } from 'react';
import { motion, useInView } from 'framer-motion';
import { MonitorIcon, CheckCircleIcon, ArrowRightIcon, ChevronRightIcon, ZapIcon, DatabaseIcon, CodeIcon, SearchIcon, MessageSquareIcon, BarChartIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Header } from '../../components/Header';
import { Footer } from '../../components/Footer';
import { ContactForm } from '../../components/ContactForm';
import { HeroLeadForm } from '../../components/HeroLeadForm';
import { ClientLogosStrip } from '../../components/ClientLogosStrip';
import { TestimonialsSection } from '../../components/TestimonialsSection';
import { FaqSection } from '../../components/FaqSection';
import { useSEO } from '../../hooks/useSEO';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslations } from '../../i18n/translations';
function HeroSection() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  return <section className="relative pt-14 lg:pt-16 pb-12 lg:pb-14 overflow-hidden bg-gradient-to-br from-violet-900 via-violet-800 to-navy-900" data-id="element-1811" data-mp="24btxka1usz4h">
      <div className="absolute inset-0" data-id="element-1812" data-mp="1zetso2gqq2ey">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-violet-500/10 blur-3xl hidden sm:block" data-id="element-1813" data-mp="2d4nfygclkemk" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-primary-500/8 blur-3xl hidden sm:block" data-id="element-1814" data-mp="28dow56segyek" />
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,1) 1px, transparent 0)`,
        backgroundSize: '32px 32px'
      }} data-id="element-1815" data-mp="2n19yuzld4vly" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6" data-id="element-1816" data-mp="2s1o98oafshn5">
        <motion.nav initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="flex items-center gap-1.5 text-sm text-violet-300/70 mb-6" data-id="element-1817" data-mp="11pi7swtp5nvk">
          <Link to="/" className="hover:text-violet-300 transition-colors" data-id="element-1818" data-mp="31qyejtgvtj14">
            {t.nav.home}
          </Link>
          <ChevronRightIcon className="w-3.5 h-3.5" data-id="element-1819" data-mp="36q1jzf0rxr9m" />
          <Link to="/#services" className="hover:text-violet-300 transition-colors" data-id="element-1820" data-mp="3bpnw1l4v91gu">
            {t.nav.services}
          </Link>
          <ChevronRightIcon className="w-3.5 h-3.5" data-id="element-1821" data-mp="3fr966yq0n1nc" />
          <span className="text-violet-200" data-id="element-1822" data-mp="3kpu3ap9z157k">{t.itRecruitment.title}</span>
        </motion.nav>

        <div className="max-w-4xl" data-id="element-1823" data-mp="3ph2enquv30nd">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.05
        }} className="flex items-center gap-3 mb-5" data-id="element-1824" data-mp="3phu8aw4weob0">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-400 to-violet-600 text-white shadow-xl shadow-violet-500/30" data-id="element-1825" data-mp="1ru9ofmjcxsiu">
              <MonitorIcon className="w-5 h-5" data-id="element-1826" data-mp="1it24k9hl8u8a" />
            </div>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-violet-500/20 text-violet-300 text-xs font-semibold rounded-full border border-violet-500/20" data-id="element-1827" data-mp="1itbslrj2orbc">
              {t.itRecruitment.badge}
            </span>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.55,
          delay: 0.1
        }} className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-[1.08]" data-id="element-1828" data-mp="26i4jh9ku88z3">
            {t.itRecruitment.h1_1}{' '}
            <span className="text-violet-400" data-id="element-1829" data-mp="2pg799ql7m9xy">{t.itRecruitment.h1_accent}</span>
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.18
        }} className="mt-4 text-base sm:text-lg text-violet-200/80 leading-relaxed" data-id="element-1830" data-mp="2a1lh99bxw588">
            {t.itRecruitment.subtitle}
          </motion.p>
        </div>

        <div className="mt-6 lg:mt-8" data-id="element-1831" data-mp="2xo1q6e7y5cix">
          <HeroLeadForm positions={t.itRecruitment.formPositions} source="it_recruitment_hero" variant="violet" data-id="element-1832" data-mp="2xr61rwnqrajm" />
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.3
      }} className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-id="element-1833" data-mp="2nxyxkf4qm0l4">
          <div className="p-4 sm:p-5 rounded-2xl bg-white/5 border border-white/10 text-center" data-id="element-1834" data-mp="101isa44ymnn9">
            <div className="text-2xl sm:text-3xl font-extrabold text-white" data-id="element-1835" data-mp="1dsw8b83nt3dj">
              {t.itRecruitment.stats.days_val}
            </div>
            <div className="text-xs text-violet-300/70 mt-1" data-id="element-1836" data-mp="19sdg3cxutvat">
              {t.itRecruitment.stats.days_label}
            </div>
          </div>
          <div className="p-4 sm:p-5 rounded-2xl bg-white/5 border border-white/10 text-center" data-id="element-1837" data-mp="m3kqrzbqmlp6">
            <div className="text-2xl sm:text-3xl font-extrabold text-violet-400" data-id="element-1838" data-mp="h4pv3wso92nn">
              {t.itRecruitment.stats.stacks_val}
            </div>
            <div className="text-xs text-violet-300/70 mt-1" data-id="element-1839" data-mp="q31twm3oz6r6">
              {t.itRecruitment.stats.stacks_label}
            </div>
          </div>
          <div className="p-4 sm:p-5 rounded-2xl bg-white/5 border border-white/10 text-center" data-id="element-1840" data-mp="26jo0luhlt7vl">
            <div className="text-2xl sm:text-3xl font-extrabold text-white" data-id="element-1841" data-mp="21kktm4i2crkz">
              {t.itRecruitment.stats.closed_val}
            </div>
            <div className="text-xs text-violet-300/70 mt-1" data-id="element-1842" data-mp="143dqfk1pwev8">
              {t.itRecruitment.stats.closed_label}
            </div>
          </div>
          <div className="p-4 sm:p-5 rounded-2xl bg-white/5 border border-white/10 text-center" data-id="element-1843" data-mp="186jqub25fbts">
            <div className="text-2xl sm:text-3xl font-extrabold text-violet-400" data-id="element-1844" data-mp="1d422wabgq760">
              {t.itRecruitment.stats.success_val}
            </div>
            <div className="text-xs text-violet-300/70 mt-1" data-id="element-1845" data-mp="kfo6fz00viwk">
              {t.itRecruitment.stats.success_label}
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}
function StacksSection() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const stacks = [{
    category: 'Frontend',
    tags: ['React', 'Vue', 'Angular', 'Next.js', 'TypeScript', 'Svelte', 'Nuxt.js', 'Gatsby', 'Tailwind CSS', 'Redux', 'GraphQL', 'Webpack', 'Vite', 'Storybook', 'Web Components']
  }, {
    category: 'Backend',
    tags: ['Node.js', 'Python', 'Java', 'Go', 'PHP', 'Ruby', '.NET', 'Rust', 'C++', 'Scala', 'Kotlin', 'Spring Boot', 'Django', 'FastAPI', 'Laravel', 'Express', 'NestJS', 'gRPC']
  }, {
    category: 'Mobile',
    tags: ['iOS (Swift)', 'Android (Kotlin)', 'React Native', 'Flutter', 'Xamarin', 'Ionic', 'Objective-C', 'SwiftUI', 'Jetpack Compose', 'Expo', 'Capacitor', 'PWA', 'Mobile QA', 'App Store Optimization', 'Push Notifications']
  }, {
    category: 'DevOps & Cloud',
    tags: ['AWS', 'GCP', 'Azure', 'Docker', 'Kubernetes', 'Terraform', 'CI/CD', 'Jenkins', 'GitLab CI', 'Ansible', 'Linux', 'Nginx', 'Prometheus', 'Grafana', 'Helm', 'ArgoCD', 'Vault', 'Site Reliability']
  }, {
    category: 'Data & AI',
    tags: ['Data Science', 'ML Engineer', 'Data Engineer', 'LLM / AI', 'Python/R', 'Spark', 'Hadoop', 'Airflow', 'dbt', 'Power BI', 'Tableau', 'Data Analyst', 'MLOps', 'Computer Vision', 'NLP', 'Snowflake', 'Databricks']
  }, {
    category: 'QA & Testing',
    tags: ['QA Manual', 'QA Automation', 'Selenium', 'Cypress', 'Playwright', 'Appium', 'Postman', 'JMeter', 'TestRail', 'Load Testing', 'Performance Testing', 'Security Testing', 'API Testing', 'BDD / Cucumber', 'QA Lead']
  }, {
    category: 'Product & Management',
    tags: ['Product Manager', 'Product Owner', 'Scrum Master', 'Agile Coach', 'Business Analyst', 'Project Manager', 'Program Manager', 'Delivery Manager', 'CTO', 'VP of Engineering', 'Head of Product', 'Release Manager', 'IT Director', 'PMO', 'OKR Facilitator']
  }, {
    category: 'Design & UX',
    tags: ['UI/UX Designer', 'Product Designer', 'UX Researcher', 'Graphic Designer', 'Motion Designer', 'Figma', 'Design Lead', 'Design System', 'Interaction Designer', 'Visual Designer', 'Brand Designer', 'Illustrator', 'Web Designer', 'Design Director', 'Accessibility']
  }, {
    category: 'Marketing & Growth',
    tags: ['Digital Marketer', 'SEO/SEM', 'Performance Marketing', 'SMM', 'Content Manager', 'Email Marketing', 'Growth Hacker', 'CMO', 'Brand Manager', 'Copywriter', 'PPC Specialist', 'Affiliate Marketing', 'Marketing Analyst', 'PR Manager', 'Influencer Marketing']
  }, {
    category: 'Sales & Bizdev',
    tags: ['Sales Manager', 'Account Executive', 'BDM', 'Pre-sales', 'Customer Success', 'Partnership Manager', 'Head of Sales', 'CCO', 'SDR', 'Account Manager', 'Sales Ops', 'Revenue Manager', 'Inside Sales', 'Enterprise Sales', 'Channel Sales']
  }, {
    category: 'Security & Infra',
    tags: ['Cybersecurity', 'Penetration Tester', 'SOC Analyst', 'Security Engineer', 'Network Engineer', 'System Administrator', 'DBA', 'PostgreSQL', 'MongoDB', 'Redis', 'MySQL', 'Oracle DBA', 'Elasticsearch', 'CISO', 'Cloud Security']
  }, {
    category: 'Finance & Operations',
    tags: ['CFO', 'Financial Analyst', 'Controller', 'HR Business Partner', 'Recruiter', 'Legal Counsel', 'COO', 'Operations Manager', 'IT Procurement', 'Vendor Manager', 'Compliance Officer', 'Risk Manager', 'IT Auditor', 'Finance Director', 'People Partner']
  }];
  return <section className="py-20 lg:py-28 bg-white" id="stacks" ref={ref} data-id="element-1846" data-mp="2p5h7v5rnokda">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1847" data-mp="2h385v16199di">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-center mb-14" data-id="element-1848" data-mp="2h5t8hse5ntcb">
          <span className="text-sm font-semibold text-violet-600 tracking-wide uppercase" data-id="element-1849" data-mp="30119k5ohdnby">
            {t.itRecruitment.stacks.eyebrow}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight" data-id="element-1850" data-mp="39t6yf6jfromx">
            {t.itRecruitment.stacks.title}
          </h2>
          <p className="mt-4 text-lg text-navy-500 max-w-2xl mx-auto" data-id="element-1851" data-mp="34vx2r95gegqn">
            {t.itRecruitment.stacks.subtitle}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1852" data-mp="3estc1h36f6w9">
          {stacks.map((stack, i) => <motion.div key={stack.category} initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: i * 0.08
        }} className="p-6 rounded-2xl bg-navy-50/60 border border-navy-100" data-id="element-1853" data-mp="28nzkq16qv6s" data-mpi={i}>
              <div className="flex items-center gap-2 mb-4" data-id="element-1854" data-mp="35p4ok6tpabex">
                <CodeIcon className="w-4 h-4 text-violet-600" data-id="element-1855" data-mp="35ms0dbxcn4tf" />
                <h3 className="font-bold text-navy-900" data-id="element-1856" data-mp="3ap0voexp2iyp">{stack.category}</h3>
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-1857" data-mp="2vrqauez5w0qb">
                {stack.tags.map(tag => <span key={tag} className="text-xs font-semibold px-2.5 py-1 bg-violet-50 text-violet-700 rounded-lg border border-violet-100" data-id="element-1858" data-mp="30oqgios3n1gw">
                    {tag}
                  </span>)}
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
function WhyUsSection() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section className="py-20 lg:py-28 bg-navy-50/40" ref={ref} data-id="element-1859" data-mp="3ga1wibknv89x">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1860" data-mp="3bc0apivuouur">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-1861" data-mp="3bca6jn3ktw8d">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6
        }} data-id="element-1862" data-mp="36e8mozybtsvu">
            <span className="text-sm font-semibold text-violet-600 tracking-wide uppercase" data-id="element-1863" data-mp="1h3o6769e00hu">
              {t.itRecruitment.whyUs.eyebrow}
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-navy-900 tracking-tight" data-id="element-1864" data-mp="1lwzgjhdeqzei">
              {t.itRecruitment.whyUs.title}
            </h2>
            <p className="mt-4 text-lg text-navy-500 leading-relaxed" data-id="element-1865" data-mp="t7aed0oyotbb">
              {t.itRecruitment.whyUs.subtitle}
            </p>
            <div className="mt-8 space-y-4" data-id="element-1866" data-mp="y5k95a6rxrwe">
              <div className="flex items-start gap-3" data-id="element-1867" data-mp="133vkxqgmqch9">
                <CheckCircleIcon className="w-5 h-5 text-violet-500 flex-shrink-0 mt-0.5" data-id="element-1868" data-mp="136hqjkjymhwu" />
                <div data-id="element-1869" data-mp="184szzu6ceqej">
                  <div className="font-semibold text-navy-900" data-id="element-1870" data-mp="181580zgjqc1x">
                    {t.itRecruitment.whyUs.points[0].title}
                  </div>
                  <div className="text-sm text-navy-500 mt-0.5" data-id="element-1871" data-mp="2jhlrr4z4veul">
                    {t.itRecruitment.whyUs.points[0].desc}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-1872" data-mp="ei3skwsmu80w">
                <CheckCircleIcon className="w-5 h-5 text-violet-500 flex-shrink-0 mt-0.5" data-id="element-1873" data-mp="ejoooqulhz0v" />
                <div data-id="element-1874" data-mp="sezfnt5pxzzy">
                  <div className="font-semibold text-navy-900" data-id="element-1875" data-mp="ofi88u6qess5">
                    {t.itRecruitment.whyUs.points[1].title}
                  </div>
                  <div className="text-sm text-navy-500 mt-0.5" data-id="element-1876" data-mp="qp80lj2tyrq">
                    {t.itRecruitment.whyUs.points[1].desc}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-1877" data-mp="9j9bbzabcriw">
                <CheckCircleIcon className="w-5 h-5 text-violet-500 flex-shrink-0 mt-0.5" data-id="element-1878" data-mp="4rj0ls7gs1qy" />
                <div data-id="element-1879" data-mp="4pobe1p5tnh2">
                  <div className="font-semibold text-navy-900" data-id="element-1880" data-mp="3d6swgikrgaqa">
                    {t.itRecruitment.whyUs.points[2].title}
                  </div>
                  <div className="text-sm text-navy-500 mt-0.5" data-id="element-1881" data-mp="395h3q46lb9dv">
                    {t.itRecruitment.whyUs.points[2].desc}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-1882" data-mp="33takf9oqdqlb">
                <CheckCircleIcon className="w-5 h-5 text-violet-500 flex-shrink-0 mt-0.5" data-id="element-1883" data-mp="2p3uziabvsfdj" />
                <div data-id="element-1884" data-mp="2u2phxb5xs1x4">
                  <div className="font-semibold text-navy-900" data-id="element-1885" data-mp="2u1wi6u5eacoh">
                    {t.itRecruitment.whyUs.points[3].title}
                  </div>
                  <div className="text-sm text-navy-500 mt-0.5" data-id="element-1886" data-mp="3hq7ava0xt61o">
                    {t.itRecruitment.whyUs.points[3].desc}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="space-y-4" data-id="element-1887" data-mp="3csxe19b7xs3t">
            <div className="p-6 rounded-2xl bg-white border border-navy-100 shadow-sm" data-id="element-1888" data-mp="1dnd5mgxs7slu">
              <div className="flex items-center gap-3 mb-3" data-id="element-1889" data-mp="18ojqkk2ujtjk">
                <div className="w-10 h-10 rounded-xl bg-violet-100 text-violet-600 flex items-center justify-center" data-id="element-1890" data-mp="18r4vjhyfyplj">
                  <ZapIcon className="w-5 h-5" data-id="element-1891" data-mp="21dpnokmqr5q5" />
                </div>
                <div className="font-bold text-navy-900" data-id="element-1892" data-mp="21g3e2g6h7trd">
                  {t.itRecruitment.whyUs.cards[0].title}
                </div>
              </div>
              <p className="text-sm text-navy-500" data-id="element-1893" data-mp="1rn4e625j4gsg">
                {t.itRecruitment.whyUs.cards[0].desc}
              </p>
            </div>
            <div className="p-6 rounded-2xl bg-white border border-navy-100 shadow-sm" data-id="element-1894" data-mp="1nkhvorur087x">
              <div className="flex items-center gap-3 mb-3" data-id="element-1895" data-mp="2k5rul49kz7n7">
                <div className="w-10 h-10 rounded-xl bg-violet-100 text-violet-600 flex items-center justify-center" data-id="element-1896" data-mp="2fdi6zuvxujo3">
                  <DatabaseIcon className="w-5 h-5" data-id="element-1897" data-mp="1wffz3815wrv" />
                </div>
                <div className="font-bold text-navy-900" data-id="element-1898" data-mp="6tfd2omwjp6u">
                  {t.itRecruitment.whyUs.cards[1].title}
                </div>
              </div>
              <p className="text-sm text-navy-500" data-id="element-1899" data-mp="axwskbm8bgbt">
                {t.itRecruitment.whyUs.cards[1].desc}
              </p>
            </div>
            <div className="p-6 rounded-2xl bg-white border border-navy-100 shadow-sm" data-id="element-1900" data-mp="kurs8xu8nmro">
              <div className="flex items-center gap-3 mb-3" data-id="element-1901" data-mp="psjizoj5d2ua">
                <div className="w-10 h-10 rounded-xl bg-violet-100 text-violet-600 flex items-center justify-center" data-id="element-1902" data-mp="pqymvuh6pbub">
                  <MessageSquareIcon className="w-5 h-5" data-id="element-1903" data-mp="yldcapvfi2r5" />
                </div>
                <div className="font-bold text-navy-900" data-id="element-1904" data-mp="ynhdh01y0nnq">
                  {t.itRecruitment.whyUs.cards[2].title}
                </div>
              </div>
              <p className="text-sm text-navy-500" data-id="element-1905" data-mp="18km53fek5bbx">
                {t.itRecruitment.whyUs.cards[2].desc}
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}
function CtaBanner() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 20
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.5
  }} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1906" data-mp="1bmn22a78esgx">
      <div className="p-8 sm:p-12 rounded-3xl bg-gradient-to-br from-violet-900 via-violet-800 to-navy-900 relative overflow-hidden" data-id="element-1907" data-mp="1ubjvycypviym">
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-violet-500/15 blur-3xl hidden sm:block" data-id="element-1908" data-mp="1zc7l0t3dxpq7" />
        <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-primary-400/10 blur-3xl hidden sm:block" data-id="element-1909" data-mp="24aiyrewswkdp" />
        <div className="relative z-10 flex flex-col sm:flex-row items-center gap-6 sm:gap-10" data-id="element-1910" data-mp="3opytz602q7h8">
          <div className="flex-1" data-id="element-1911" data-mp="3jv1oua5x0i9m">
            <h3 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight" data-id="element-1912" data-mp="3etc9iibntifq">
              {t.itRecruitment.ctaBanner.title}
            </h3>
            <p className="mt-2 text-violet-200/80 leading-relaxed" data-id="element-1913" data-mp="39wly48asvb0n">
              {t.itRecruitment.ctaBanner.subtitle}
            </p>
          </div>
          <a href="#contact" className="flex-shrink-0 inline-flex items-center gap-2 px-7 py-4 text-base font-semibold text-violet-900 bg-white rounded-xl hover:bg-violet-50 transition-all shadow-lg hover:-translate-y-0.5" data-id="element-1914" data-mp="6diwidgjep3f">
            {t.itRecruitment.ctaBanner.button}
            <ArrowRightIcon className="w-4 h-4" data-id="element-1915" data-mp="u3wltw7qm9ub" />
          </a>
        </div>
      </div>
    </motion.div>;
}
const seoData = {
  UA: {
    title: 'IT Recruitment — Підбір IT-спеціалістів | ESCA',
    description: 'Підбір IT-спеціалістів будь-якого рівня: розробники, DevOps, QA, Product Manager, дизайнери. 50+ технологічних стеків, 1500+ закритих IT-вакансій.',
    keywords: 'IT рекрутинг, підбір IT спеціалістів, розробники, DevOps, QA, React, Python, Java, ESCA'
  },
  RU: {
    title: 'IT Recruitment — Подбор IT-специалистов | ESCA',
    description: 'Подбор IT-специалистов любого уровня: разработчики, DevOps, QA, Product Manager, дизайнеры. 50+ технологических стеков, 1500+ закрытых IT-вакансий.',
    keywords: 'IT рекрутинг, подбор IT специалистов, разработчики, DevOps, QA, React, Python, Java, ESCA'
  },
  EN: {
    title: 'IT Recruitment — Hire Top Tech Talent | ESCA',
    description: 'IT recruitment for all levels: developers, DevOps, QA, Product Managers, designers. 50+ tech stacks covered, 1500+ IT positions filled.',
    keywords: 'IT recruitment, tech hiring, developers, DevOps, QA, React, Python, Java, ESCA'
  }
};
export function ITRecruitmentPage() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const seo = seoData[lang];
  useSEO({
    title: seo.title,
    description: seo.description,
    keywords: seo.keywords
  });
  return <div className="min-h-screen w-full bg-white overflow-x-hidden" data-id="element-1916" data-mp="1od82dcwm3nh5">
      <Header data-id="element-1917" data-mp="1jkon8bia0ka8" />
      <main className="pt-16 lg:pt-20" data-id="element-1918" data-mp="1jk6mb1ko5hvz">
        <HeroSection data-id="element-1919" data-mp="1jj3u8qbm19eh" />
        <ClientLogosStrip data-id="element-1920" data-mp="1xbtu9ynz4eul" />
        <StacksSection data-id="element-1921" data-mp="1xbblk2k4k85s" />
        <WhyUsSection data-id="element-1922" data-mp="1xaigd60tdqpr" />
        <TestimonialsSection data-id="element-1923" data-mp="1sda6rttqev04" />
        <FaqSection items={t.itRecruitment.faqItems} accent="violet" data-id="element-1924" data-mp="1se1ruappdac5" />
        <CtaBanner data-id="element-1925" data-mp="27aj02t2li8uh" />
        <div id="contact" data-id="element-1926" data-mp="27b2jyfbt37b5">
          <ContactForm data-id="element-1927" data-mp="2798074c9tl8n" />
        </div>
      </main>
      <Footer data-id="element-1928" data-mp="22bg561gbxj08" />
    </div>;
}